/**
 * Phase 9.1 — DrillDownContext.
 *
 * Global provider that owns:
 *   - "currently open" state for the two modals (Product + Order).
 *   - a lazy-loaded map from employee code (Z18, 004, ...) to canonical_id,
 *     so tables that only carry the raw code can still link to /employees/{id}
 *     without extra API round-trips per row.
 *
 * Modals themselves are rendered ONCE inside `App.js` layer so they layer
 * correctly (nested product-inside-order or order-inside-product) without
 * unmount cascades.
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/api/client";

const DrillDownCtx = createContext({
  productSku: null,
  orderId: null,
  openProductModal: () => {},
  openOrderModal: () => {},
  closeProductModal: () => {},
  closeOrderModal: () => {},
  resolveCanonicalId: () => null,
});

export function DrillDownProvider({ children }) {
  const [productSku, setProductSku] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [codeMap, setCodeMap] = useState({});

  // Load the alias → canonical_id map once. This is a small payload
  // (a few hundred canonicals) so we cache it in-memory for the session.
  useEffect(() => {
    let cancelled = false;
    api
      .get("/employees-canonical", { params: { limit: 1000 } })
      .then(({ data }) => {
        if (cancelled) return;
        const m = {};
        for (const c of data || []) {
          const cid = c.id;
          const nameNorm = (c.canonical_name_norm || c.canonical_name || "").trim().toLowerCase();
          if (nameNorm) m[`name:${nameNorm}`] = cid;
          for (const a of c.aliases || []) {
            if (a?.code) m[`code:${String(a.code).trim().toLowerCase()}`] = cid;
          }
        }
        setCodeMap(m);
      })
      .catch(() => {
        /* keep an empty map; clickable helpers gracefully degrade to plain text */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const openProductModal = useCallback((sku) => setProductSku(sku || null), []);
  const openOrderModal = useCallback((oid) => setOrderId(oid || null), []);
  const closeProductModal = useCallback(() => setProductSku(null), []);
  const closeOrderModal = useCallback(() => setOrderId(null), []);

  /** Resolve an employee code OR name to a canonical id. Returns null if unknown. */
  const resolveCanonicalId = useCallback(
    (codeOrName) => {
      if (!codeOrName) return null;
      const key = String(codeOrName).trim().toLowerCase();
      return codeMap[`code:${key}`] || codeMap[`name:${key}`] || null;
    },
    [codeMap],
  );

  return (
    <DrillDownCtx.Provider
      value={{
        productSku,
        orderId,
        openProductModal,
        openOrderModal,
        closeProductModal,
        closeOrderModal,
        resolveCanonicalId,
      }}
    >
      {children}
    </DrillDownCtx.Provider>
  );
}

export function useDrillDown() {
  return useContext(DrillDownCtx);
}
